import type { LeadSegment } from "@/lib/db-types";
import naceCodes from "@/lib/nace-codes.json";

/** SN2007-koder på nivå 5 (Enhetsregisterets næringskode), hentet fra SSBs Klass. */
export const NACE_CODES: readonly (readonly [string, string])[] = naceCodes as [string, string][];

/** Søk i kode og navn (alle ord må treffe), maks `limit` treff. */
export function searchNace(query: string, limit = 12): { code: string; name: string }[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const out: { code: string; name: string }[] = [];
  for (const [code, name] of NACE_CODES) {
    const hay = `${code} ${name}`.toLowerCase();
    if (words.every((w) => hay.includes(w))) {
      out.push({ code, name });
      if (out.length >= limit) break;
    }
  }
  return out;
}

/** Skjema-tekst til tall: tom gir null, ugyldig gir undefined. Mellomrom og komma tillates. */
export function parseOptionalNumber(raw: string): number | null | undefined {
  const t = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

export interface SegmentFormState {
  naeringskode: string[];
  fylke: string[];
  kommunenummer: string;
  employeesMin: string;
  employeesMax: string;
  revenueMin: string;
  revenueMax: string;
}

export const EMPTY_SEGMENT_FORM: SegmentFormState = {
  naeringskode: [],
  fylke: [],
  kommunenummer: "",
  employeesMin: "",
  employeesMax: "",
  revenueMin: "",
  revenueMax: "",
};

/** Bygger LeadSegment fra skjemaet. Gir feilmelding når et tall er ugyldig. */
export function buildSegment(form: SegmentFormState): { segment: LeadSegment } | { error: string } {
  const numbers = {
    employees_min: parseOptionalNumber(form.employeesMin),
    employees_max: parseOptionalNumber(form.employeesMax),
    revenue_min_nok: parseOptionalNumber(form.revenueMin),
    revenue_max_nok: parseOptionalNumber(form.revenueMax),
  };
  const bad = Object.entries(numbers).find(([, v]) => v === undefined);
  if (bad) return { error: "Ansatte og omsetning må være tall" };
  const kommuner = form.kommunenummer.split(/[\s,;]+/).map((k) => k.trim()).filter(Boolean);
  if (kommuner.some((k) => !/^\d{4}$/.test(k))) return { error: "Kommunenummer må være fire sifre" };
  const segment: LeadSegment = {
    naeringskode: form.naeringskode,
    fylke: form.fylke,
    kommunenummer: kommuner,
    employees_min: numbers.employees_min as number | null,
    employees_max: numbers.employees_max as number | null,
    revenue_min_nok: numbers.revenue_min_nok as number | null,
    revenue_max_nok: numbers.revenue_max_nok as number | null,
  };
  return { segment };
}

/** Skjemaet for et lagret segment (agentens segment). Manglende felt blir tomme. */
export function segmentToForm(segment: LeadSegment | null | undefined): SegmentFormState {
  if (!segment) return { ...EMPTY_SEGMENT_FORM };
  const n = (v: number | null | undefined) => (typeof v === "number" ? String(v) : "");
  return {
    naeringskode: segment.naeringskode ?? [],
    fylke: segment.fylke ?? [],
    kommunenummer: (segment.kommunenummer ?? []).join(", "),
    employeesMin: n(segment.employees_min),
    employeesMax: n(segment.employees_max),
    revenueMin: n(segment.revenue_min_nok),
    revenueMax: n(segment.revenue_max_nok),
  };
}

/** Et segment uten bransje, fylke eller kommune ville truffet hele registeret. */
export function hasSegmentFilter(segment: LeadSegment | null | undefined): boolean {
  return !!segment && ((segment.naeringskode?.length ?? 0) > 0 || (segment.fylke?.length ?? 0) > 0 || (segment.kommunenummer?.length ?? 0) > 0);
}
